import {
  TextInput,
  Textarea,
  Button,
  Group,
  Title,
  Card,
  Stack,
  Divider,
  Text,
  Box,
  Loader,
  Image,
  Modal,
  Alert,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { Dropzone, FileWithPath } from "@mantine/dropzone";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { HiOutlineUpload } from "react-icons/hi";

type CompanyFieldsRef = {
  companyBrandName: React.RefObject<HTMLInputElement>;
  yourName: React.RefObject<HTMLInputElement>;
  yourEmail: React.RefObject<HTMLInputElement>;
  logoDisplayDate: React.RefObject<HTMLButtonElement>;
  websiteUrl: React.RefObject<HTMLInputElement>;
};

interface CompanyInfoFormProps {
  form: any; // Replace with proper type from @mantine/form
  fieldRefs: CompanyFieldsRef,
  handleSubmit: (values: any) => void;
  loading?: boolean;
  error?: string | null;
  setError?: (error: string | null) => void;
}



const CompanyInfoForm: React.FC<CompanyInfoFormProps> = ({
  form,
  fieldRefs,
  handleSubmit,
  loading = false,
  error,
}) => {
  const [modalImage, setModalImage] = useState<string | null>(null);
  const [modalImageName, setModalImageName] = useState<string | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);
  const [rasterImagesError, setRasterImagesError] = useState<string | null>(
    null,
  );

  const getPreviewUrl = (file: File) => URL.createObjectURL(file);

  return (
    <>
      <Card shadow="md" p="lg" radius="md" withBorder>
        <Title order={2} mb="xs" >
          Partners: Submit Company Information for Audinate Website
        </Title>

        <Divider mb="md" />

        <Text size="xl" fw={700} mb="sm">
          Welcome to the Dante Partner Community!
        </Text>

        <Text size="sm" mb="sm">
          By sharing your company details, you&apos;ll be featured on our{' '}
          <Text
            component="a"
            href="https://www.getdante.com/products/dante-enabled/dante-manufacturers-list/"
            target="_blank"
            c="blue"
            span
          >
            Dante Licensed Manufacturers
          </Text>{' '}
          page, and your logo will appear alongside your products in the{' '}
          <Text
            component="a"
            href="https://www.getdante.com/products/dante-enabled/"
            target="_blank"
            c="blue"
            span
          >
            Dante-enabled Product Catalog
          </Text>
          .
        </Text>

        <Text size="sm" mb="lg">
          Want to showcase your products on our website?{' '}
          <Text
            component="a"
            href="/product-submission"
            c="blue"
            span
          >
            Click here to submit your products
          </Text>
          .
        </Text>

        <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            <Divider label="Company Information" labelPosition="center" />

            <TextInput
              ref={fieldRefs.companyBrandName}
              label="Company (brand name)"
              placeholder="Enter company name"
              {...form.getInputProps("companyBrandName")}
              withAsterisk
            />
            <Group
              grow
              align="flex-start"
            >
              <TextInput
                ref={fieldRefs.yourName}
                label="Your Name"
                placeholder="Enter your name"
                {...form.getInputProps("yourName")}
                withAsterisk
              />
              <TextInput
                ref={fieldRefs.yourEmail}
                label="Your Email"
                placeholder="you@example.com"
                type="email"
                {...form.getInputProps("yourEmail")}
                error={form.errors.yourEmail}
                withAsterisk
              />
            </Group>
            <input type="hidden" {...form.getInputProps("submitterId")} />
            <Group
              grow
              align="flex-start"
            >
              <DatePickerInput
                ref={fieldRefs.logoDisplayDate}
                label="When may we display your logo on the Audinate website?"
                placeholder="Select date"
                value={form.values.logoDisplayDate ? new Date(form.values.logoDisplayDate) : null}
                onChange={(date) => form.setFieldValue("logoDisplayDate", date)}
                error={form.errors.logoDisplayDate}
                withAsterisk
                minDate={new Date()}
                valueFormat="MM/DD/YYYY"
                clearable
              />
              <TextInput
                ref={fieldRefs.websiteUrl}
                label="Your main website URL"
                placeholder="https://example.com"
                {...form.getInputProps("websiteUrl")}
                error={form.errors.websiteUrl}
                withAsterisk
              />
            </Group>
            <Textarea
              label="Company Description (about 75 words)"
              placeholder="Describe your company"
              minRows={4}
              autosize
              value={form.values.companyDescription}
              onChange={(event) => {
                const text = event.currentTarget.value;

                // Handle empty text case
                if (text.trim() === "") {
                  form.setFieldValue("companyDescription", text);
                  form.setFieldError("companyDescription", null);
                  return;
                }

                const words = text.trim().split(/\s+/);
                const currentWordCount = words.length;

                if (currentWordCount <= 75) {
                  form.setFieldValue("companyDescription", text);
                  form.setFieldError("companyDescription", null);
                } else {
                  // Truncate to exactly 75 words and show error
                  const limited = words.slice(0, 75).join(" ");
                  form.setFieldValue("companyDescription", limited);
                  form.setFieldError("companyDescription", "Maximum 75 words reached");
                }
              }}
              onKeyDown={(event) => {
                // Prevent typing when already at word limit
                const currentText = form.values.companyDescription || "";
                if (currentText.trim() === "") return; // Allow typing in empty field

                const words = currentText.trim().split(/\s+/);
                const isAtLimit = words.length >= 75;

                // Allow navigation keys, delete/backspace, and selection
                const allowedKeys = [
                  'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight',
                  'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab', 'Escape'
                ];

                // Allow Ctrl/Cmd combinations (copy, paste, select all, etc.)
                const isCtrlCmd = event.ctrlKey || event.metaKey;

                if (isAtLimit && !allowedKeys.includes(event.key) && !isCtrlCmd) {
                  event.preventDefault();
                }
              }}
              error={form.errors.companyDescription}
            />

            <Divider label="Logo Uploads" labelPosition="center" />

            {/* Logo Image Upload */}
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Logo Image{" "}
                <Text span c="red">
                  *
                </Text>
              </Text>
              <Stack gap="xs" style={{ maxWidth: 1150 }}>
                <Text size="xs" c="dimmed">
                  Image must be 600px wide minimum, preferably on a white background. Formats: JPG, JPEG, PNG. Max 4MB. Include company name in filename.
                </Text>
                {/* <Text size="xs" c="dimmed">
                  Images must be 600 px wide minimum and on a white background (preferably). This should be a raster image file such as jpeg, png or tif.
                </Text>
                <Text size="xs" c="dimmed">
                  Please include your company name in the file name.
                </Text>
                <Text size="xs" c="dimmed">
                  Files must be less than 4MB.
                </Text>
                <Text size="xs" c="dimmed">
                  Allowed file types: jpg jpeg png tif
                </Text> */}
              </Stack>
              {form.values.rasterImages?.length > 0 && (
                <Card withBorder radius="md" padding="sm" bg="white">
                  <Stack gap="xs">
                    {form.values.rasterImages.map(
                      (file: FileWithPath, index: number) => (
                        <Group
                          key={index}
                          justify="space-between"
                          align="center"
                        >
                          <Group gap="sm" align="center">
                            <Image
                              src={getPreviewUrl(file)}
                              alt={`Raster preview ${index + 1}`}
                              w={50}
                              h={50}
                              fit="cover"
                              radius="sm"
                            />
                            <Text size="sm" fw={500} truncate>
                              {file.name} (
                              {(file.size / 1024 / 1024).toFixed(2)} MB)
                            </Text>
                          </Group>
                          <Group gap="xs">
                            <Button
                              size="xs"
                              variant="light"
                              color="blue"
                              onClick={() => {
                                setModalImage(getPreviewUrl(file));
                                setModalImageName(file.name);
                                open();
                              }}
                            >
                              View
                            </Button>
                            <Button
                              size="xs"
                              variant="outline"
                              color="red"
                              disabled={loading}
                              onClick={() => {
                                const updated = form.values.rasterImages.filter(
                                  (_: File, i: number) => i !== index,
                                );
                                form.setFieldValue("rasterImages", updated);
                                setRasterImagesError(null);
                              }}
                            >
                              Remove
                            </Button>
                          </Group>
                        </Group>
                      ),
                    )}
                  </Stack>
                </Card>
              )}
              {form.values.rasterImages?.length === 0 && (
                <Dropzone
                  onDrop={(files) => {
                    if (loading) return; // Prevent file changes during submission

                    // Only allow one file
                    if (files.length > 0) {
                      setRasterImagesError(null);
                      form.setFieldValue("rasterImages", [files[0]]);
                    }
                  }}
                  onReject={(fileRejections) => {
                    if (loading) return; // Prevent file changes during submission

                    const rejection = fileRejections[0];
                    if (!rejection) return;

                    const isTooLarge = rejection.errors.some((e) => e.code === "file-too-large");
                    const isInvalidType = rejection.errors.some((e) => e.code === "file-invalid-type");

                    if (isTooLarge) {
                      setRasterImagesError(
                        `The file ${rejection.file.name} is too large (over 4MB)`,
                      );
                    } else if (isInvalidType) {
                      setRasterImagesError(
                        `The file ${rejection.file.name} is invalid (only JPG, JPEG, PNG allowed)`,
                      );
                    } else {
                      setRasterImagesError("The file is invalid.");
                    }
                  }}
                  maxSize={4 * 1024 ** 2}
                  accept={['image/jpeg', 'image/png']}
                  multiple={false}
                  disabled={loading}
                  style={{
                    borderRadius: 12,
                    backgroundColor: loading ? "#f1f3f4" : "#f8f9fa",
                    border: "2px dashed #ced4da",
                    opacity: loading ? 0.6 : 1,
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  <Group justify="center" gap="xs" style={{ minHeight: 100 }}>
                    <HiOutlineUpload size={40} color={loading ? "#adb5bd" : "#495057"} />
                    <Text ta="center" c={loading ? "dimmed" : "inherit"}>
                      {loading ? "Submitting..." : "Drag & drop or click to upload Logo Image"}
                      <br />
                      <small>{loading ? "Please wait..." : "(One image only - JPG, JPEG, PNG, Max 4MB)"}</small>
                    </Text>
                  </Group>
                </Dropzone>
              )}
              {rasterImagesError && (
                <Text c="red" size="sm" ta="center">
                  {rasterImagesError}
                </Text>
              )}
              {form.errors.rasterImages && (
                <Text c="red" size="sm" ta="center">
                  {form.errors.rasterImages}
                </Text>
              )}
            </Stack>

            <Divider label="Additional Information" labelPosition="center" />

            <Textarea
              label="Additional Comments"
              placeholder="Any additional comments"
              minRows={4}
              autosize
              {...form.getInputProps("additionalComments")}
            />

            <Group justify="flex-end" mt="sm">
              {error && (
                <Alert color="red" mb="xs">
                  {error}
                </Alert>
              )}
              <Button
                type="submit"
                loading={loading}
                disabled={loading || Object.keys(form.errors).length > 0}
                leftSection={
                  loading ? <Loader size="xs" color="white" /> : null
                }
              >
                {loading ? "Submitting..." : "Submit Company Information"}
              </Button>
            </Group>
          </Stack>
        </Box>
      </Card>

      <Modal
        opened={opened}
        onClose={() => {
          setZoomed(false);
          close();
        }}
        title={modalImageName ? `Viewing: ${modalImageName}` : "View Image"}
        size="auto"
        centered
        overlayProps={{ blur: 3 }}
        styles={{
          body: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: zoomed ? "zoom-out" : "zoom-in",
          },
        }}
      >
        {modalImage && (
          <Image
            src={modalImage}
            alt="Full image"
            style={{
              maxWidth: zoomed ? "none" : "100%",
              maxHeight: zoomed ? "none" : "80vh",
              transform: zoomed ? "scale(1.5)" : "scale(1)",
              transition: "transform 0.3s ease",
            }}
            onClick={() => setZoomed(!zoomed)}
          />
        )}
      </Modal>
    </>
  );
};

export default CompanyInfoForm;
